import { createSlice } from "@reduxjs/toolkit";

import { clearStore } from "actions/initialization";
import localStorageService from "services/localStorage";
import {
  conversionOrderThunk,
  conversionRedoThunk,
  conversionsListThunk,
  createRecordingTTSThunk,
  createRecordingThunk,
  deleteRecordingThunk,
  exportTakeThunk,
  recordingsListThunk,
  updateRecordingThunk,
  fetchRecordingThunk,
  conversionCountThunk,
} from "thunks/recording";
import { logger } from "utils/logger";
import {
  GMT_TIME_IDENTIFIER,
  LOCALSTORAGE_ITEMS,
  ORIGINAL_TYPE,
  PAGINATION,
  RECORDINGS_SORT_KEYS,
  RECORDING_STATE,
  SORT_DIRECTION,
  initialConversionListPaginationParams,
  initialRecordingListPaginationParams,
} from "../constants";

/*
FIXME store all recordings in global hash (originals + conversions)
      but store ids and like in current implementation
      (better control for loading/errors, not sticked to original)
*/
const conversionInitialState = {
  ...initialConversionListPaginationParams,
  ids: [],
  hash: {},
  loading: {
    list: false,
    create: false,
    update: false,
    delete: false,
    convert: false,
    export: false,
  },
  error: { create: null, update: null, delete: null, convert: null },
};
export const initialState = {
  originals: {
    ...initialRecordingListPaginationParams,
    sort: {
      key: Object.keys(RECORDINGS_SORT_KEYS)
        .map((k) => RECORDINGS_SORT_KEYS[k].key)
        .includes(
          localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.recordings.key)
        )
        ? localStorageService.getItem(LOCALSTORAGE_ITEMS.sort.recordings.key)
        : RECORDINGS_SORT_KEYS.CREATED_AT_DESC.key,
      direction: Object.keys(SORT_DIRECTION)
        .map((k) => SORT_DIRECTION[k])
        .includes(
          localStorageService.getItem(
            LOCALSTORAGE_ITEMS.sort.recordings.direction
          )
        )
        ? localStorageService.getItem(
            LOCALSTORAGE_ITEMS.sort.recordings.direction
          )
        : SORT_DIRECTION.desc,
    },
    ids: [],
    hash: {},
    uploaded:
      localStorageService.getItem(LOCALSTORAGE_ITEMS.newOriginalData)
        ?.uploaded || [],
    newOriginalData: localStorageService.getItem(
      LOCALSTORAGE_ITEMS.newOriginalData
    ) || {
      projectId: undefined,
      folderId: undefined,
      projectUrl: undefined,
      calibrationId: undefined,
      ignoreClear: false,
      type: ORIGINAL_TYPE.tts,
    },
    loading: {
      fetch: false,
      create: false,
      update: false,
      delete: false,
      export: false,
      conversionCount: false,
    },
    cachedAt: {},
    error: {
      fetch: null,
      create: null,
      update: null,
      delete: null,
      convert: null,
      conversionCount: null,
    },
    conversionCount: {},
  },
  conversions: {
    // ...initialConversionListPaginationParams,
    /*
      [originalId]: 
        ids: [],
        hash: {},
        pagination: { ... }
        loading: {
          list: false,
          create: false,
          update: false,
          delete: false,
          convert: false,
          export: false,
        },
        error: { create: null, update: null, delete: null, convert: null },
    */
  },
  tmpStages: new Map(),
  selectedOriginal: null,
};

const proceedProcessStage = (process_stage) => {
  const [process_current, tmpTotalSteps] = (process_stage || "1/1").split("/");
  const process_total = parseInt(tmpTotalSteps || 0) + 1;
  const process_deg =
    process_stage === "0"
      ? 0
      : !process_stage
      ? 100
      : (100 / process_total) * parseInt(process_current);
  return {
    process_current: parseInt(process_current),
    process_total,
    process_deg,
  };
};

export const recordingDTO = (recording) => {
  const { process_current, process_total, process_deg } = proceedProcessStage(
    recording.process_stage
  );
  return {
    id: recording.id,
    projectId: recording.project_id,
    folderId: recording.parent_folder_id,
    url: recording.url,
    waveformUrl: recording.waveform_url,
    name: recording.file_name,
    file_name: recording.file_name,
    label: recording.label,
    state: recording.state,
    type: recording.type,
    originalId: recording.original_id,
    modelId: recording.model_id,
    microphone: recording.microphone,
    error: recording.error,
    active: recording.active,
    size: recording.size,
    starred: recording.starred,
    tts: recording.tts,
    tts_voice: recording.tts_voice,
    tts_voice_id: recording.tts_voice_id,
    text: recording.text,
    text_language: recording.text_language,
    createdAt: new Date(`${recording.created_at}${GMT_TIME_IDENTIFIER}`),
    createdAtTimestamp: Date.parse(
      `${recording.created_at}${GMT_TIME_IDENTIFIER}`
    ),
    convertedAt: recording.converted_at
      ? new Date(`${recording.converted_at}${GMT_TIME_IDENTIFIER}`)
      : null,
    convertedAtTimestamp: recording.converted_at
      ? Date.parse(`${recording.converted_at}${GMT_TIME_IDENTIFIER}`)
      : null,
    params: recording.params,
    isNew: false,
    duration: recording.duration,
    calibration_id: recording.calibration_id,
    calibration_value: recording.calibration_value,
    calibration_name: recording.calibration_name,
    process_stage: recording.process_stage,
    process_current,
    process_total,
    process_deg,
    voice_id: recording.voice_id,
    voice_name: recording.voice_name,
    voice_talent: recording.voice_talent,
    semitones_correction: recording.semitones_correction,
    accent_id: recording.accent_id,
    accent_name: recording.accent_name,
    narration_style_id: recording.narration_style_id,
    narration_style_name: recording.narration_style_name,
    loading: {
      update: false,
      export: false,
    },
    moderation: recording.moderation,
  };
};

const recordingSlice = createSlice({
  name: "recording",
  initialState,
  reducers: {
    recordingsListSetPaginationData(state, action) {
      const { pagination, sort } = action.payload;
      if (pagination) {
        const { count, currentPage, limit } = pagination;
        if (currentPage) {
          state.originals.pagination.currentPage = currentPage;
          state.originals.pagination.offset =
            (currentPage - 1) * state.originals.pagination.limit;
        }
        if (count) {
          state.originals.pagination.count = count;
        }
        if (limit) {
          state.originals.pagination.limit = limit;
        }
      }
      if (sort) {
        state.originals.sort = sort;
      }
      state.originals.pagination.forceFetch = true;
    },
    recordingConversionReady(state, action) {
      const conversion = action.payload;
      const { original_id, id } = conversion;
      if (
        state.conversions[original_id] &&
        state.conversions[original_id].ids.includes(id)
      ) {
        state.conversions[original_id].hash[conversion.id] =
          recordingDTO(conversion);
      }
      if (!original_id && state.originals.hash[id]) {
        state.originals.hash[id] = recordingDTO(conversion);
      }
    },
    updateRecordingStage(state, action) {
      // FIXME
      const { recordingId, originalId, stage } = action.payload;
      if (
        state.conversions[originalId] &&
        state.conversions[originalId].hash[recordingId]
      ) {
        state.conversions[originalId].hash[recordingId].process_stage = stage;
        const { process_current, process_total, process_deg } =
          proceedProcessStage(stage);
        state.conversions[originalId].hash[recordingId].process_current =
          process_current;
        state.conversions[originalId].hash[recordingId].process_total =
          process_total;
        state.conversions[originalId].hash[recordingId].process_deg =
          process_deg;
      }
      // else {
      //   state.tmpStages.set(recordingId, stage);
      // }
    },
    conversionsListSetPaginationData(state, action) {
      const { originalId, pagination } = action.payload;
      if (pagination && originalId) {
        const { count, currentPage, limit } = pagination;
        if (!state.conversions[originalId]) {
          state.conversions[originalId] = {
            ...conversionInitialState,
          };
        }
        if (currentPage) {
          state.conversions[originalId].pagination = {
            ...state.conversions[originalId].pagination,
            currentPage,
            offset:
              (currentPage - 1) *
              state.conversions[originalId].pagination.limit,
          };
          // state.conversions[originalId].pagination.currentPage = currentPage;
          // state.conversions[originalId].pagination.offset =
          //   (currentPage - 1) * state.conversions[originalId].pagination.limit;
        }
        if (count) {
          state.conversions[originalId].pagination.count = count;
        }
        if (limit) {
          state.conversions[originalId].pagination.limit = limit;
        }
        state.conversions[originalId].pagination.forceFetch = true;
      }
    },
    recordingSetSelectedOriginal(state, action) {
      const { originalId } = action.payload;
      state.selectedOriginal = originalId;
    },
    addOriginalsToConvert(state, action) {
      const ids = action.payload;
      state.originals.uploaded = ids;
      if (state.originals.newOriginalData?.uploaded) {
        state.originals.newOriginalData.uploaded = ids;
      }
    },
    setNewOriginalData(state, action) {
      const data = action.payload;
      state.originals.newOriginalData = data;
    },
    clearNewOriginalData(state, action) {
      state.originals.newOriginalData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(recordingsListThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { folderId },
          },
        } = action;
        state.originals.loading[folderId] = true;
        state.originals.uploaded = [];
      })
      .addCase(createRecordingThunk.pending, (state) => {
        state.originals.loading.create = true;
      })
      .addCase(createRecordingTTSThunk.pending, (state) => {
        state.originals.loading.create = true;
      })
      .addCase(updateRecordingThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { recordingId },
          },
        } = action;
        // FIXME conversions
        if (state.originals.ids.includes(recordingId)) {
          state.originals.loading.update = true;
          state.originals.hash[recordingId].update = true;
        }
      })
      .addCase(deleteRecordingThunk.pending, (state) => {
        state.originals.loading.delete = true;
      })
      .addCase(conversionOrderThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
        } = action;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.convert = true;
        }
      })
      .addCase(exportTakeThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
        } = action;
        state.originals.loading.export = true;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.export = true;
        }
      })
      .addCase(conversionRedoThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { originalId, recordingId },
          },
        } = action;
        if (state.conversions[originalId]) {
          state.conversions[originalId].loading.convert = true;
          if (state.conversions[originalId].hash[recordingId]) {
            state.conversions[originalId].hash[recordingId].state =
              RECORDING_STATE.PROCESSING;
          }
        }
      })
      .addCase(conversionsListThunk.pending, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
        } = action;
        if (!state.conversions[originalId]) {
          state.conversions[originalId] = {
            ...conversionInitialState,
          };
        }
        state.conversions[originalId].loading.list = true;
      })
      .addCase(fetchRecordingThunk.pending, (state) => {
        state.originals.loading.fetch = true;
      })
      .addCase(conversionCountThunk.pending, (state) => {
        state.originals.loading.conversionCount = true;
      })
      // #endregion
      // #region fulfilled
      .addCase(recordingsListThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { folderId },
          },
          payload: recordings,
        } = action;
        state.originals.loading[folderId] = false;
        const { list, pagination } = recordings;
        const ids = [];
        list.forEach((recording) => {
          ids.push(recording.id);
          state.originals.hash[recording.id] = recordingDTO(recording);
        });
        state.originals.ids = ids;
        const { limit, ...otherPaginationData } = pagination;
        state.originals.pagination = {
          ...state.originals.pagination,
          ...otherPaginationData,
          totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
          forceFetch: false,
        };
        state.originals.cachedAt[folderId] = Date.now();
      })
      .addCase(createRecordingThunk.fulfilled, (state, action) => {
        const recording = action.payload;
        state.originals.loading.create = false;
        state.originals.uploaded.unshift(recording.id);
        state.originals.ids.unshift(recording.id);
        state.originals.hash[recording.id] = recordingDTO(recording);
        state.originals.hash[recording.id].isNew = true;
        if (state.originals.ids.length > PAGINATION.recordings) {
          state.originals.ids.pop();
        }
        state.originals.pagination = {
          ...state.originals.pagination,
          count: state.originals.pagination.count + 1,
          offset: 0,
          currentPage: 1,
        };
        if (
          state.originals.pagination.count > state.originals.pagination.limit
        ) {
          state.originals.pagination.totalPages =
            Math.ceil(
              state.originals.pagination.count /
                state.originals.pagination.limit
            ) || 1;
        }
        //
        state.conversions = {
          ...initialState.conversions,
        };
        state.selectedOriginal = recording.id;
        //
        if (state.originals.newOriginalData) {
          state.originals.newOriginalData = {
            ...state.originals.newOriginalData,
            uploaded: [
              ...(state.originals.newOriginalData.uploaded || []),
              recording.id,
            ],
          };
        }
      })
      .addCase(createRecordingTTSThunk.fulfilled, (state, action) => {
        const recording = action.payload;
        state.originals.loading.create = false;
        state.originals.uploaded.unshift(recording.id);
        state.originals.ids.unshift(recording.id);
        state.originals.hash[recording.id] = recordingDTO(recording);
        state.originals.hash[recording.id].isNew = true;
        if (state.originals.ids.length > PAGINATION.recordings) {
          state.originals.ids.pop();
        }
        state.originals.pagination = {
          ...state.originals.pagination,
          count: state.originals.pagination.count + 1,
          offset: 0,
          currentPage: 1,
        };
        if (
          state.originals.pagination.count > state.originals.pagination.limit
        ) {
          state.originals.pagination.totalPages =
            Math.ceil(
              state.originals.pagination.count /
                state.originals.pagination.limit
            ) || 1;
        }
        //
        state.conversions = {
          ...initialState.conversions,
        };
        state.selectedOriginal = recording.id;
      })
      // FIXME check this on conversions, refactor
      .addCase(updateRecordingThunk.fulfilled, (state, action) => {
        const recordingData = action.payload;
        state.originals.loading.update = false;
        const { id, original_id, parent_folder_id, starred, label } =
          recordingData;
        if (state.originals.hash[id]) {
          state.originals.hash[id] = {
            ...state.originals.hash[id],
            parent_folder_id,
            starred,
            label,
            loading: {
              ...state.originals.hash[id].loading,
              update: false,
            },
          };
        }
        if (
          original_id &&
          state.conversions[original_id] &&
          state.conversions[original_id].hash[id]
        ) {
          state.conversions[original_id].hash[id] = {
            ...state.conversions[original_id].hash[id],
            parent_folder_id,
            starred,
            label,
            loading: {
              ...state.conversions[original_id].hash[id].loading,
              update: false,
            },
          };
        }
      })
      // FIXME check this on conversions, refactor
      .addCase(deleteRecordingThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          payload: _recordingIds,
        } = action;
        const recordingIds = _recordingIds.map((item) => item.id);
        state.originals.loading.delete = false;
        recordingIds.forEach((recordingId) => {
          delete state.originals.hash[recordingId];
        });
        state.originals.ids = state.originals.ids.filter(
          (id) => !recordingIds.includes(id)
        );
        state.originals.uploaded = state.originals.uploaded.filter(
          (id) => !recordingIds.includes(id)
        );
        // sync pagination
        if (
          state.originals.pagination.currentPage ===
            state.originals.pagination.totalPages &&
          state.originals.pagination.currentPage > 1 &&
          state.originals.ids.length === 0
        ) {
          state.originals.pagination.currentPage =
            state.originals.pagination.currentPage - 1;
          state.originals.pagination.offset =
            (state.originals.pagination.currentPage - 1) *
            state.originals.pagination.limit;
        }
        if (
          state.originals.pagination.currentPage !==
            state.originals.pagination.totalPages &&
          state.originals.ids.length < state.originals.pagination.limit
        ) {
          state.originals.pagination.forceFetch = true;
        }
        if (
          state.originals.pagination.currentPage === 1 &&
          state.originals.ids.length !== state.originals.pagination.count
        ) {
          state.originals.pagination.count = state.originals.ids.length;
        }
        // conversions
        if (originalId && state.conversions[originalId]) {
          state.conversions[originalId].ids = state.conversions[
            originalId
          ].ids.filter((id) => !recordingIds.includes(id));
          if (
            state.conversions[originalId].pagination.currentPage ===
              state.conversions[originalId].pagination.totalPages &&
            state.conversions[originalId].pagination.currentPage > 1 &&
            state.conversions[originalId].ids.length === 0
          ) {
            state.conversions[originalId].pagination.currentPage =
              state.conversions[originalId].pagination.currentPage - 1;
            state.conversions[originalId].pagination.offset =
              (state.conversions[originalId].pagination.currentPage - 1) *
              state.conversions[originalId].pagination.limit;
          }
          state.conversions[originalId].pagination.forceFetch = true;
          if (
            state.conversions[originalId].pagination.currentPage === 1 &&
            state.conversions[originalId].ids.length !==
              state.conversions[originalId].pagination.count
          ) {
            state.conversions[originalId].pagination.count =
              state.conversions[originalId].ids.length;
          }
        }
      })
      .addCase(exportTakeThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
        } = action;
        state.originals.loading.export = false;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.export = false;
        }
      })
      // FIXME
      .addCase(conversionOrderThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          payload: conversions,
        } = action;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.convert = false;
        }
        // obsolete? we're just forwarding user to conversion list
        /*
        if (state.selectedOriginal === conversions[0].original_id) {
          let counter = 0;
          const number = conversions[0].number || 0;
          const page = Math.ceil(number / state.conversions.pagination.limit);
          conversions.forEach((conversion) => {
            state.hash[conversion.id] = recordingDTO(conversion);
            if (!state.conversions.ids.includes(conversion.id)) {
              if (
                (conversion.number || 0) <
                  state.conversions.pagination.offset +
                    state.conversions.pagination.limit &&
                page === state.conversions.pagination.currentPage
              ) {
                state.conversions.ids.unshift(conversion.id);
                if (state.conversions.ids.length > PAGINATION.conversions) {
                  state.conversions.ids.pop();
                }
              }
              counter = counter + 1;
            }
          });
          state.conversions.pagination = {
            ...state.conversions.pagination,
            count: state.conversions.pagination.count + counter,
            offset: (page - 1) * state.conversions.pagination.limit,
            currentPage: page,
            forceFetch: page !== state.conversions.pagination.currentPage,
          };
        }
        */
      })
      .addCase(conversionRedoThunk.fulfilled, (state, action) => {})
      .addCase(conversionsListThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          payload: conversions,
        } = action;
        state.conversions[originalId].loading.list = false;
        const { list, pagination } = conversions;
        const ids = [];
        list.forEach((recording) => {
          ids.push(recording.id);
          state.conversions[originalId].hash[recording.id] =
            recordingDTO(recording);
        });
        state.conversions[originalId].ids = ids;
        const { limit, ...otherPaginationData } = pagination;
        state.conversions[originalId].pagination = {
          ...state.conversions[originalId].pagination,
          ...otherPaginationData,
          totalPages: Math.ceil(otherPaginationData.count / limit) || 1,
          forceFetch: false,
        };
      })
      .addCase(fetchRecordingThunk.fulfilled, (state, action) => {
        const recording = action.payload;
        state.originals.loading.fetch = false;
        state.originals.ids.unshift(recording.id);
        state.originals.hash[recording.id] = recordingDTO(recording);
      })
      .addCase(conversionCountThunk.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.originals.loading.conversionCount = false;
        Object.keys(data).forEach((voiceId) => {
          state.originals.conversionCount[voiceId] = data[voiceId];
        });
      })
      // #endregion
      // #region rejected
      .addCase(recordingsListThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { folderId },
          },
          payload: error,
        } = action;
        state.originals.loading[folderId] = false;
        state.originals.error[folderId] = error;
      })
      .addCase(createRecordingThunk.rejected, (state, action) => {
        const { error } = action;
        state.originals.loading.create = false;
        state.originals.error.create = error;
      })
      .addCase(createRecordingTTSThunk.rejected, (state, action) => {
        const { error } = action;
        state.originals.loading.create = false;
        state.originals.error.create = error;
      })
      // FIXME
      .addCase(updateRecordingThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { recordingId },
          },
          error,
        } = action;
        // FIXME conversions
        if (state.originals.ids.includes(recordingId)) {
          state.originals.loading.update = false;
          state.originals.hash[recordingId].loading.update = false;
          state.originals.error.update = error;
        }
      })
      // FIXME
      .addCase(deleteRecordingThunk.rejected, (state, action) => {
        const { error } = action;
        state.originals.loading.delete = false;
        state.originals.error.delete = error;
      })
      .addCase(exportTakeThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          error,
        } = action;
        state.originals.loading.export = false;
        state.originals.error.export = error;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.export = false;
        }
      })
      .addCase(conversionOrderThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          error,
        } = action;
        if (state.originals.hash[originalId]) {
          state.originals.hash[originalId].loading.convert = false;
        }
        // track errors for each original?
        // state.originals.hash[originalId].error.convert = error;
        state.originals.error.convert = error;
      })
      .addCase(conversionRedoThunk.rejected, (state, action) => {
        logger.info("action", action);
        const {
          meta: {
            arg: { originalId, recordingId },
          },
          error,
        } = action;
        if (state.conversions[originalId]) {
          state.conversions[originalId].loading.convert = false;
          state.conversions[originalId].error.convert = error;
        }
      })
      .addCase(conversionsListThunk.rejected, (state, action) => {
        const {
          meta: {
            arg: { originalId },
          },
          error,
        } = action;
        state.conversions[originalId].loading.list = false;
        state.conversions[originalId].error.list = error;
      })
      .addCase(fetchRecordingThunk.rejected, (state, action) => {
        const { error } = action;
        state.originals.loading.fetch = false;
        state.originals.error.fetch = error;
      })
      .addCase(conversionCountThunk.rejected, (state, action) => {
        const { error } = action;
        state.originals.loading.conversionCount = false;
        state.originals.error.conversionCount = error;
      })
      // #endregion
      .addCase(clearStore, () => initialState);
  },
});

export const selectPlayingRecording = (state, originalId) => {
  const currentId = state.player.current;

  if (currentId) {
    return originalId
      ? state.recording.conversions[originalId].hash[currentId]
      : state.recording.originals.hash[currentId];
  }
  return null;
};

export const selectRecordingInfo = (recordings, recordingId, originalId) => {
  if (!recordingId) return { starred: null, state: null };

  // TODO recordings.hash
  const rec = originalId
    ? recordings.conversions[originalId].hash[recordingId]
    : recordings.originals.hash[recordingId];

  if (!rec) return;
  return {
    starred: rec.starred,
    state: rec.state,
    errorText: rec.error,
    name: rec.name,
    url: rec.url,
    params: rec.params,
    duration: rec.duration,
    process_stage: rec.process_stage,
    active: rec.active,
    file_name: rec.file_name,
  };
};

export const actions = recordingSlice.actions;

export default recordingSlice.reducer;
